import { useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  useMutationDeleteDatasetTimeseries,
  useMutationPutDatasetTimeseries,
  useValidateTimeseriesAttributes
} from "@/api/dataset/useDatasetAPI";
import {
  getSavedTimeAttributeRows,
  getTimeAttributeOptions,
  getTimeseriesErrorMessage,
  hasNumericAttribute,
  TimeAttributeRowValue
} from "@/components/datasetManagement/details/dataSettingsV2/timeSeriesConfig";
import NotificationContent from "@/components/notification/notificationContent";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { DatasetAttributeSchema, TimeseriesFormat } from "@/types/dataset";

type UseTimeSeriesConfigParams = {
  datasetId: string;
  schema: DatasetAttributeSchema[];
  /** Called once the configuration is written. */
  onSaved: () => void;
};

const useTimeSeriesConfig = ({
  datasetId,
  schema,
  onSaved
}: UseTimeSeriesConfigParams) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const newRowCount = useRef(0);

  const createEmptyRow = (): TimeAttributeRowValue => {
    newRowCount.current += 1;
    return { key: `new-${newRowCount.current}` };
  };

  // What was just saved, kept until a reloaded schema replaces the one it was saved on.
  const [justSaved, setJustSaved] = useState<{
    schema: DatasetAttributeSchema[];
    rows: TimeAttributeRowValue[];
  } | null>(null);
  const savedRows =
    justSaved?.schema === schema
      ? justSaved.rows
      : getSavedTimeAttributeRows(schema);

  const getInitialRows = () =>
    savedRows.length ? savedRows : [createEmptyRow()];

  const [rows, setRows] = useState<TimeAttributeRowValue[]>(getInitialRows);

  const attributeOptions = useMemo(
    () => getTimeAttributeOptions(schema),
    [schema]
  );
  const isNumericMissing = !hasNumericAttribute(schema);

  // Only rows the user changed are checked; saved rows passed when they were saved.
  const validations = useValidateTimeseriesAttributes(
    datasetId,
    rows.map((row) => (row.isTouched ? row : {}))
  );
  const { mutate: mutatePutTimeseries, isPending: isPutting } =
    useMutationPutDatasetTimeseries(datasetId);
  const { mutate: mutateDeleteTimeseries, isPending: isDeleting } =
    useMutationDeleteDatasetTimeseries(datasetId);
  const isSaving = isPutting || isDeleting;
  const hasSavedConfig = savedRows.length > 0;
  // Every saved row was removed, so saving clears the configuration.
  const isRemovingConfig = rows.length === 0 && hasSavedConfig;

  // An attribute picked in one row is not offered in the others.
  const optionsByRow = rows.map((row) =>
    attributeOptions.filter(
      (option) =>
        option.value === row.attributeId ||
        !rows.some((other) => other.attributeId === option.value)
    )
  );

  // A failed check keeps any older answer in `data`, so it must not count.
  const errorByRow = validations.map((validation) =>
    validation.isError
      ? DATA_SETTINGS_MESSAGE.TIMESERIES_ERROR_FALLBACK
      : getTimeseriesErrorMessage(validation.data)
  );

  // A changed row counts only once its latest check has passed.
  const isCheckedValidByRow = validations.map(
    (validation) =>
      validation.isSuccess &&
      !validation.isFetching &&
      validation.data?.valid === true
  );

  const isEveryRowValid = rows.every(
    (row, index) =>
      row.attributeId &&
      row.format &&
      (!row.isTouched || isCheckedValidByRow[index])
  );

  // Rows are compared with what is saved, since removing a row leaves nothing touched.
  const toConfigKey = (list: TimeAttributeRowValue[]) =>
    JSON.stringify(list.map((row) => [row.attributeId, row.format]));
  const hasChanges = toConfigKey(rows) !== toConfigKey(savedRows);

  const canSave =
    !isSaving &&
    hasChanges &&
    (isRemovingConfig ||
      (rows.length > 0 && isEveryRowValid && !isNumericMissing));
  const canAddRow = rows.length < attributeOptions.length;

  const addRow = () => setRows((prev) => [...prev, createEmptyRow()]);

  const removeRow = (key: string) =>
    setRows((prev) => prev.filter((row) => row.key !== key));

  const updateRow = (
    key: string,
    patch: { attributeId?: string; format?: TimeseriesFormat }
  ) =>
    setRows((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, ...patch, isTouched: true } : row
      )
    );

  /** Back to what the dataset holds, e.g. when the view is opened again. */
  const resetRows = () => setRows(getInitialRows());

  const handleSaved = (savedRowsNow: TimeAttributeRowValue[]) => {
    setJustSaved({ schema, rows: savedRowsNow });
    queryClient.invalidateQueries({
      queryKey: ["dataset-metadata", datasetId]
    });
    queryClient.invalidateQueries({ queryKey: ["dataset-data", datasetId] });
    queryClient.invalidateQueries({
      queryKey: ["dataset", datasetId, "schema"]
    });
    showToast({
      type: EToastType.SUCCESS,
      message: (
        <NotificationContent
          title={DATA_SETTINGS_MESSAGE.TIMESERIES_SAVE_SUCCESS_TITLE}
          description={
            savedRowsNow.length
              ? DATA_SETTINGS_MESSAGE.TIMESERIES_SAVE_SUCCESS_DESCRIPTION
              : DATA_SETTINGS_MESSAGE.TIMESERIES_REMOVE_SUCCESS_DESCRIPTION
          }
        />
      )
    });
    onSaved();
  };

  const showSaveFailed = () =>
    showToast({
      type: EToastType.ERROR,
      title: DATA_SETTINGS_MESSAGE.TIMESERIES_SAVE_FAILED_TITLE,
      message: DATA_SETTINGS_MESSAGE.TIMESERIES_SAVE_FAILED_DESCRIPTION
    });

  const save = () => {
    if (!canSave) return;
    if (isRemovingConfig) {
      mutateDeleteTimeseries(undefined, {
        onSuccess: () => handleSaved([]),
        onError: showSaveFailed
      });
      return;
    }
    mutatePutTimeseries(
      {
        attributes: rows.map((row) => ({
          id: row.attributeId as string,
          format: row.format as TimeseriesFormat
        }))
      },
      {
        onSuccess: () =>
          handleSaved(rows.map((row) => ({ ...row, isTouched: false }))),
        onError: showSaveFailed
      }
    );
  };

  return {
    rows,
    savedAttributeCount: savedRows.length,
    attributeOptions,
    optionsByRow,
    errorByRow,
    isCheckedValidByRow,
    isNumericMissing,
    canAddRow,
    canSave,
    isSaving,
    /** The first row can go only when there is a saved configuration to clear. */
    canRemoveFirstRow: hasSavedConfig,
    addRow,
    removeRow,
    updateRow,
    resetRows,
    save
  };
};

export type TimeSeriesConfig = ReturnType<typeof useTimeSeriesConfig>;

export default useTimeSeriesConfig;
